import * as React from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type Edge as XYEdge,
  type EdgeProps,
  useReactFlow,
} from '@xyflow/react';
import type { Node } from '@xyflow/react';
import { getSmoothStepPath } from '@xyflow/system';
import { cn } from '@shared/utils/cn';
import { useCanvasStore } from '@features/flows/state/canvas-store';
import { summarizeEdgeMapping, type EdgeMappingSummary } from '@features/flows/lib/autocast/mapping-summary';
import { parseHandleId } from '@features/flows/utils/edge';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import type { CanvasState } from '@features/flows/state/canvas-store';
import { deriveCoarseTypeFromPin as deriveCoarseTypeFromPinSpec } from '@features/flows/utils/pins';
import { TYPE_COLOR_TOKENS, colorVarForType } from './edge-colors';
import { Loader2, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type EdgeCurve = 'default' | 'smoothstep';

// Prefer informative coarse type candidates; treat "any" as a fallback.
const coalesceType = (...candidates: (string | undefined)[]) => {
  for (const candidate of candidates) {
    if (candidate && candidate.toLowerCase() !== 'any') {
      return candidate;
    }
  }
  return candidates.find(Boolean);
};

const shallowEqual = (a: Record<string, unknown>, b: Record<string, unknown>) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!Object.is(a[key], b[key])) return false;
  }
  return true;
};

const getCoarseTypeForPin = (
  state: CanvasState,
  nodeId?: string,
  handle?: { channel: 'data' | 'exec'; io: 'in' | 'out'; raw: string } | null,
): string | undefined => {
  if (!nodeId || !handle || handle.channel !== 'data') {
    return undefined;
  }
  const node = state.graph.nodes.find((n) => n.id === nodeId);
  if (!node) {
    return undefined;
  }
  const pins = (node.data as any)?.pins;
  const collection = handle.io === 'in' ? pins?.data?.in : pins?.data?.out;
  if (!Array.isArray(collection)) {
    return undefined;
  }
  const pin = collection.find((p: any) => p.id === handle.raw);
  return pin ? deriveCoarseTypeFromPinSpec(pin) : undefined;
};

const signatureSeparator = '@@';
const SPLIT_RATIO_PERCENT = 50;

function DataEdgeComponent(props: EdgeProps) {
  const rf = useReactFlow();
  const templates = useCanvasStore((state) => state.templates);
  const isDraggingNodes = useCanvasStore((state) => state.ui.isDraggingNodes);
  const sourceNode = React.useMemo<Node | null>(() => {
    try {
      return props.source ? (rf.getNode(props.source as any) as Node | undefined) ?? null : null;
    } catch {
      return null;
    }
  }, [rf, props.source]);
  const targetNode = React.useMemo<Node | null>(() => {
    try {
      return props.target ? (rf.getNode(props.target as any) as Node | undefined) ?? null : null;
    } catch {
      return null;
    }
  }, [rf, props.target]);
  const rawCurve = ((props.data as any)?.curve ?? 'default') as 'default' | 'smoothstep' | 'step';
  const curve: EdgeCurve = rawCurve === 'smoothstep' ? 'smoothstep' : 'default';
  const onCurveChange = React.useCallback(
    (value: EdgeCurve) => {
      try {
        useCanvasStore.getState().patchEdge(props.id, { curve: value });
      } catch {}
    },
    [props.id],
  );
  const onOpenMapper = React.useCallback(() => {
    try {
      const store = useCanvasStore.getState();
      store.select({ edges: [props.id] });
      store.openMapper(props.id, 'liquid');
    } catch {}
  }, [props.id]);
  const onRemove = React.useCallback(() => {
    try {
      useCanvasStore.getState().removeEdge(props.id);
    } catch {}
  }, [props.id]);

  // Try to infer coarse type from source/target handles and their nodes' pin specs
  const rawSourceHandle = (props as any).sourceHandleId ?? (props as any).sourceHandle ?? null;
  const rawTargetHandle = (props as any).targetHandleId ?? (props as any).targetHandle ?? null;

  const dataChannel = (props.data as any)?.channel as ('data' | 'exec') | undefined;
  const dataRawSourceHandle = (props.data as any)?.rawSourceHandle as string | undefined;
  const dataRawTargetHandle = (props.data as any)?.rawTargetHandle as string | undefined;

  const sourceHandle = React.useMemo(() => {
    const parsed = parseHandleId(rawSourceHandle ?? undefined);
    if (parsed) return parsed;
    if ((dataChannel === 'data' || dataChannel === 'exec') && dataRawSourceHandle) {
      return { channel: dataChannel, io: 'out' as const, raw: dataRawSourceHandle };
    }
    return null;
  }, [rawSourceHandle, dataChannel, dataRawSourceHandle]);
  const targetHandle = React.useMemo(() => {
    const parsed = parseHandleId(rawTargetHandle ?? undefined);
    if (parsed) return parsed;
    if ((dataChannel === 'data' || dataChannel === 'exec') && dataRawTargetHandle) {
      return { channel: dataChannel, io: 'in' as const, raw: dataRawTargetHandle };
    }
    return null;
  }, [rawTargetHandle, dataChannel, dataRawTargetHandle]);
  const sourceHandleKey = sourceHandle ? `${sourceHandle.channel}:${sourceHandle.io}:${sourceHandle.raw}` : '';
  const targetHandleKey = targetHandle ? `${targetHandle.channel}:${targetHandle.io}:${targetHandle.raw}` : '';

  const computePinSignature = React.useCallback(
    (state: CanvasState) => {
      const sourceCoarse = getCoarseTypeForPin(state, props.source, sourceHandle);
      const targetCoarse = getCoarseTypeForPin(state, props.target, targetHandle);
      const src = sourceCoarse ?? '';
      const tgt = targetCoarse ?? '';
      return `${src}${signatureSeparator}${tgt}`;
    },
    [props.source, props.target, sourceHandleKey, targetHandleKey],
  );

  const [pinSignature, setPinSignature] = React.useState(() => computePinSignature(useCanvasStore.getState()));

  React.useEffect(() => {
    const selector = (state: CanvasState) => computePinSignature(state);
    setPinSignature(selector(useCanvasStore.getState()));
    const unsub = useCanvasStore.subscribe(selector, (next) => {
      setPinSignature(next);
    });
    return () => {
      unsub();
    };
  }, [computePinSignature]);

  const [sourceCoarseFromStore, targetCoarseFromStore] = React.useMemo(() => {
    const [srcRaw, tgtRaw] = pinSignature.split(signatureSeparator);
    return [srcRaw || undefined, tgtRaw || undefined] as const;
  }, [pinSignature]);

  const resolveHandleType = React.useCallback(
    (nodeId?: string, handle?: { channel: 'data' | 'exec'; io: 'in' | 'out'; raw: string }): string | undefined => {
      if (!nodeId || !handle || handle.channel !== 'data') return undefined;
      const node = rf.getNode(nodeId as any) as any;
      const pins = node?.data?.pins;
      const collection = handle.io === 'in' ? pins?.data?.in : pins?.data?.out;
      const pin = Array.isArray(collection) ? collection.find((p: any) => p.id === handle.raw) : undefined;
      return pin ? deriveCoarseTypeFromPinSpec(pin) : undefined;
    },
    [rf],
  );

  const data = (props.data as any) || {};
  const dataType = data?.type as string | undefined;
  const dataCoarse = data?.coarse as string | undefined;
  const dataTypeLabel = data?.typeLabel as string | undefined;
  const sourceCoarsePersisted = data?.sourceCoarse as string | undefined;
  const targetCoarsePersisted = data?.targetCoarse as string | undefined;

  const sourceDataType = React.useMemo(
    () =>
      coalesceType(
        sourceCoarseFromStore,
        resolveHandleType(props.source, sourceHandle),
        sourceCoarsePersisted,
      ),
    [
      sourceCoarseFromStore,
      resolveHandleType,
      props.source,
      sourceHandle,
      sourceCoarsePersisted,
    ],
  );
  const targetDataType = React.useMemo(
    () =>
      coalesceType(
        targetCoarseFromStore,
        resolveHandleType(props.target, targetHandle),
        targetCoarsePersisted,
      ),
    [
      targetCoarseFromStore,
      resolveHandleType,
      props.target,
      targetHandle,
      targetCoarsePersisted,
    ],
  );

  const inferCoarseType = React.useMemo(() => {
    return coalesceType(
      targetDataType,
      sourceDataType,
      sourceCoarsePersisted,
      targetCoarsePersisted,
      dataType,
      dataCoarse,
      'any',
    );
  }, [
    targetDataType,
    sourceDataType,
    sourceCoarsePersisted,
    targetCoarsePersisted,
    dataType,
    dataCoarse,
  ]);

  const edgeColorVar = React.useMemo(() => colorVarForType(inferCoarseType), [inferCoarseType]);
  const sourceColorVar = React.useMemo(() => colorVarForType(sourceDataType), [sourceDataType]);
  const targetColorVar = React.useMemo(() => colorVarForType(targetDataType), [targetDataType]);

  const [path, labelX, labelY] = curve === 'smoothstep' ? getSmoothStepPath(props) : getBezierPath(props);

  const mappingSpec = (data?.mappingSpec as unknown) ?? null;
  const mappingJSONata = typeof data?.mappingJSONata === 'string' ? data.mappingJSONata : null;

  const summaryEdge = React.useMemo(
    () =>
      ({
        id: props.id,
        source: props.source,
        target: props.target,
        sourceHandle: rawSourceHandle ?? undefined,
        targetHandle: rawTargetHandle ?? undefined,
        data: {
          mappingSpec,
          mappingJSONata,
        },
      }) as unknown as XYEdge,
    [props.id, props.source, props.target, rawSourceHandle, rawTargetHandle, mappingSpec, mappingJSONata],
  );

  const summaryNodes = React.useMemo(() => {
    const collection: Record<string, Node> = {};
    if (sourceNode) collection[sourceNode.id] = sourceNode;
    if (targetNode) collection[targetNode.id] = targetNode;
    return collection;
  }, [sourceNode, targetNode]);

  const lastSummaryRef = React.useRef<EdgeMappingSummary | null>(null);
  const mappingSummary = React.useMemo(() => {
    if (isDraggingNodes) {
      return lastSummaryRef.current;
    }
    const next = summarizeEdgeMapping(summaryEdge, summaryNodes, templates);
    lastSummaryRef.current = next;
    return next;
  }, [isDraggingNodes, summaryEdge, summaryNodes, templates]);

  const fallbackLabel =
    typeof data.path === 'string' && data.path.trim()
      ? data.path.length > 22
        ? `${data.path.slice(0, 22)}…`
        : data.path
      : data.typeLabel ?? inferCoarseType;
  const shouldRenderFallback = Boolean(fallbackLabel) && !TYPE_COLOR_TOKENS.has(String(fallbackLabel).toLowerCase());
  const mappingLLMRunning = Boolean(data?.mappingLLMRunning);
  const mappingLLMError = data?.mappingLLMError as string | undefined;

  const showSplitColor =
    Boolean(sourceColorVar) &&
    Boolean(targetColorVar) &&
    sourceColorVar !== targetColorVar;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <DataEdgeTrigger
          edgeId={props.id}
          path={path}
          stroke={edgeColorVar}
          sourceStroke={showSplitColor ? sourceColorVar : null}
          targetStroke={targetColorVar}
          selected={props.selected ?? false}
          sourceX={props.sourceX}
          sourceY={props.sourceY}
          targetX={props.targetX}
          targetY={props.targetY}
        />
      </ContextMenuTrigger>
      <ContextMenuContent>
        <DataEdgeMenu
          curve={curve}
          onCurveChange={onCurveChange}
          onOpenMapper={onOpenMapper}
          onRemove={onRemove}
        />
      </ContextMenuContent>
      {mappingSummary && (
        <EdgeLabelRenderer>
          <div
            style={{ position: 'absolute', transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
            className="pointer-events-auto"
          >
            <button
              type="button"
              aria-label={`${mappingSummary.from} to ${mappingSummary.to}${
                mappingSummary.multi > 1 ? ` plus ${mappingSummary.multi - 1} additional mappings` : ''
              }`}
              title={`${mappingSummary.from} → ${mappingSummary.to}${
                mappingSummary.multi > 1 ? ` (+${mappingSummary.multi - 1})` : ''
              }`}
              onClick={(event) => {
                event.stopPropagation();
                onOpenMapper();
              }}
              className={cn(
'flex items-center gap-1 rounded-full border bg-background/95 px-2 py-0.5 text-[10px] font-mono shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
                props.selected && 'ring-1 ring-primary/50 border-primary/60'
              )}
            >
              <span
                aria-hidden
                className="inline-block h-2 w-2 rounded-full border"
                style={{ background: sourceColorVar }}
              />
              <span className="max-w-[220px] truncate">
                {mappingSummary.from} → {mappingSummary.to}
                {mappingSummary.multi > 1 ? ` (+${mappingSummary.multi - 1})` : ''}
              </span>
              <span
                aria-hidden
                className="inline-block h-2 w-2 rounded-full border"
                style={{ background: targetColorVar }}
              />
            </button>
          </div>
        </EdgeLabelRenderer>
      )}
      {!mappingSummary && shouldRenderFallback && (
        <EdgeLabelRenderer>
          <TooltipProvider delayDuration={0}>
            <div
              style={{ position: 'absolute', transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
              className={cn(
                'flex items-center gap-1 rounded border bg-background/90 px-1.5 py-0.5 text-[10px] font-mono shadow-sm',
                props.selected && 'ring-1 ring-primary/50'
              )}
            >
              {mappingLLMRunning && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
              {!mappingLLMRunning && mappingLLMError && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="pointer-events-auto">
                      <AlertCircle className="h-2.5 w-2.5 text-red-500" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="pointer-events-auto">
                    <p className="text-xs max-w-xs">{mappingLLMError}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {fallbackLabel}
            </div>
          </TooltipProvider>
        </EdgeLabelRenderer>
      )}
    </ContextMenu>
  );
}

const DataEdge = React.memo(
  DataEdgeComponent,
  (prev, next) =>
    prev.id === next.id &&
    prev.source === next.source &&
    prev.target === next.target &&
    prev.sourceX === next.sourceX &&
    prev.sourceY === next.sourceY &&
    prev.targetX === next.targetX &&
    prev.targetY === next.targetY &&
    prev.selected === next.selected &&
    prev.sourceHandle === next.sourceHandle &&
    prev.targetHandle === next.targetHandle &&
    prev.data === next.data,
);

export default DataEdge;

type DataEdgeTriggerProps = {
  edgeId: string;
  path: string;
  stroke: string;
  sourceStroke: string | null;
  targetStroke: string | null;
  selected: boolean;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
} & React.ComponentPropsWithoutRef<'g'>;

const DataEdgeTrigger = React.memo(
  React.forwardRef<SVGGElement, DataEdgeTriggerProps>(function DataEdgeTrigger(
    {
      edgeId,
      path,
      stroke,
      sourceStroke,
      targetStroke,
      selected,
      sourceX,
      sourceY,
      targetX,
      targetY,
      ...rest
    },
    ref,
  ) {
    const hasSplitStroke = Boolean(sourceStroke) && Boolean(targetStroke) && sourceStroke !== targetStroke;

    return (
      <g ref={ref} {...rest}>
        <BaseEdge id={edgeId} path={path} style={{ strokeWidth: 2, stroke: targetStroke ?? stroke }} />
        {hasSplitStroke && (
          <path
            d={path}
            stroke={sourceStroke!}
            strokeWidth={2}
            fill="none"
            pathLength={100}
            strokeDasharray={`${SPLIT_RATIO_PERCENT} ${100 - SPLIT_RATIO_PERCENT}`}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pointerEvents: 'none' }}
          />
        )}
        {selected && (
          <path
            d={path}
            stroke={'hsl(var(--primary))'}
            strokeOpacity={0.6}
            strokeWidth={8}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            style={{ pointerEvents: 'none' }}
          />
        )}
        {selected && (
          <>
            <circle cx={sourceX} cy={sourceY} r={4.5} fill={'hsl(var(--primary))'} fillOpacity={0.7} />
            <circle cx={targetX} cy={targetY} r={4.5} fill={'hsl(var(--primary))'} fillOpacity={0.7} />
          </>
        )}
      </g>
    );
  }),
  (prev, next) =>
    prev.edgeId === next.edgeId &&
    prev.path === next.path &&
    prev.stroke === next.stroke &&
    prev.sourceStroke === next.sourceStroke &&
    prev.targetStroke === next.targetStroke &&
    prev.selected === next.selected &&
    prev.sourceX === next.sourceX &&
    prev.sourceY === next.sourceY &&
    prev.targetX === next.targetX &&
    prev.targetY === next.targetY &&
    shallowEqual(
      (({ edgeId, path, stroke, selected, sourceX, sourceY, targetX, targetY, ...restProps }) => restProps)(prev),
      (({ edgeId, path, stroke, selected, sourceX, sourceY, targetX, targetY, ...restProps }) => restProps)(next),
    ),
);

DataEdgeTrigger.displayName = 'DataEdgeTrigger';

type DataEdgeMenuProps = {
  curve: EdgeCurve;
  onCurveChange: (value: EdgeCurve) => void;
  onOpenMapper: () => void;
  onRemove: () => void;
};

const DataEdgeMenu = React.memo(function DataEdgeMenu({
  curve,
  onCurveChange,
  onOpenMapper,
  onRemove,
}: DataEdgeMenuProps) {
  const handleCurveChange = React.useCallback(
    (value: string) => {
      onCurveChange(value === 'smoothstep' ? 'smoothstep' : 'default');
    },
    [onCurveChange],
  );

  const handleOpenMapper = React.useCallback<React.ComponentProps<typeof ContextMenuItem>['onSelect']>(
    () => {
      onOpenMapper();
    },
    [onOpenMapper],
  );

  const handleRemove = React.useCallback<React.ComponentProps<typeof ContextMenuItem>['onSelect']>(
    () => {
      onRemove();
    },
    [onRemove],
  );

  return (
    <>
      <ContextMenuLabel>Edge Type</ContextMenuLabel>
      <ContextMenuRadioGroup value={curve} onValueChange={handleCurveChange}>
        <ContextMenuRadioItem value="default">Default</ContextMenuRadioItem>
        <ContextMenuRadioItem value="smoothstep">Smoothstep</ContextMenuRadioItem>
      </ContextMenuRadioGroup>
      <ContextMenuSeparator />
      <ContextMenuItem onSelect={handleOpenMapper}>
        Open Autocast Mapper
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem onSelect={handleRemove}>
        Remove Edge
      </ContextMenuItem>
    </>
  );
});

DataEdgeMenu.displayName = 'DataEdgeMenu';
